import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import CtaBand from "@/components/CtaBand";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const ZONES = [
  {
    region: "Siège — Wattignies (Nord, 59)",
    text: "ATLAS THD est implantée à Wattignies, au cœur de la métropole lilloise. Le Nord est notre base d'opérations pour les interventions fibre optique et les missions de contrôle.",
    badge: "Siège",
  },
  {
    region: "Hauts-de-France (59, 62)",
    text: "Nord et Pas-de-Calais : proximité immédiate pour les raccordements FTTH, les interventions techniques et les inspections sur l'ensemble de la région.",
  },
  {
    region: "Bretagne (35, 22)",
    text: "Ille-et-Vilaine et Côtes-d'Armor : interventions régulières sur les raccordements et les missions de contrôle qualité dans l'ouest.",
  },
  {
    region: "Pays de la Loire (44)",
    text: "Loire-Atlantique : chantiers documentés sur les points de mutualisation, notamment à Couëron et Treillières.",
    to: "/realisations",
    linkLabel: "Voir ces réalisations",
  },
];

const ZonesIntervention = () => (
  <>
    <Seo
      title="Zones d'intervention fibre optique : Nord, Bretagne, France | ATLAS THD"
      description="ATLAS THD intervient en fibre optique dans les Hauts-de-France (59, 62), en Bretagne (35, 22), en Loire-Atlantique (44) et sur toute la France selon les missions."
      breadcrumbs={[{ label: "Accueil", to: "/" }, { label: "Zones d'intervention" }]}
    />

    <section className="border-b border-line bg-cloud" data-testid="zones-hero">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:py-20 lg:px-8">
        <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Zones d'intervention" }]} />
        <motion.p {...fadeUp} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-6 font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">
          Où nous intervenons
        </motion.p>
        <motion.h1 {...fadeUp} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="mt-4 max-w-4xl font-display text-4xl font-extrabold tracking-tight text-midnight sm:text-5xl">
          Zones d'intervention d'ATLAS THD
        </motion.h1>
        <motion.p {...fadeUp} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="mt-6 max-w-3xl text-lg leading-relaxed text-steel">
          Les zones listées ci-dessous correspondent à notre implantation et à
          nos chantiers réels. Au-delà, nos équipes se déplacent sur l'ensemble
          du territoire français selon la nature et le volume des missions —
          chaque demande fait l'objet d'une étude de faisabilité.
        </motion.p>
      </div>
    </section>

    <section className="bg-white py-20" data-testid="zones-list">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-px border border-line bg-line md:grid-cols-2">
          {ZONES.map((z, i) => (
            <motion.div key={z.region} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }} className="bg-white p-8 lg:p-10" data-testid={`zone-${i}`}>
              <div className="flex items-center gap-3">
                <MapPin className="h-6 w-6 shrink-0 text-terrain" />
                <h2 className="font-display text-xl font-bold tracking-tight text-midnight">{z.region}</h2>
                {z.badge && (
                  <span className="bg-qualite px-2 py-0.5 font-display text-xs font-bold uppercase tracking-wider text-midnight">
                    {z.badge}
                  </span>
                )}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-steel">{z.text}</p>
              {z.to && (
                <Link to={z.to} data-testid={`zone-link-${i}`} className="mt-4 inline-flex items-center gap-1.5 font-display text-sm font-bold text-terrain transition-colors hover:text-midnight">
                  {z.linkLabel}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </motion.div>
          ))}
        </div>
        <motion.p {...fadeUp} transition={{ duration: 0.5 }} className="mt-8 border border-line bg-cloud p-6 text-sm leading-relaxed text-steel" data-testid="zones-national">
          <span className="font-display font-bold text-midnight">Toute la France :</span>{" "}
          pour les campagnes de raccordements, les missions d'audit ou les
          contrats multi-sites, contactez-nous avec la localisation de vos
          chantiers — nous étudions chaque demande.
        </motion.p>
      </div>
    </section>

    <CtaBand title="Un chantier dans l'une de ces zones ?" />
  </>
);

export default ZonesIntervention;
